import { S_AXIS_DATA } from '../data/axes/sAxisData';
import { E_AXIS_DATA } from '../data/axes/eAxisData';
import { B_AXIS_DATA } from '../data/axes/bAxisData';

/**
 * Inner Analysis Engine (v2.0)
 * 
 * Generates dynamic report content based on the comparison 
 * between Reality (Mega Test) and Ideal (Beyond Test).
 * 
 * Logic Refinements:
 * 1. Gap Levels: Harmony, Tension, Conflict, Crisis
 * 2. Target Points: A(20), B(50), C(75), D(95)
 * 3. Axis Intensity: S/E Specific Ranges
 */

// --- 1. CONFIGURATION CONSTANTS ---

// Beyond Target Points (Ideal Score Mapping)
const TARGET_POINTS = {
    'A': 20, // 평온/자율/자유 (15~25)
    'B': 50, // 공명/결속/확인 (45~55)
    'C': 75, // 진화/교감/성장 (70~80)
    'D': 95  // 질서/융합/체계 (90~100)
};

// Gap Ranges (Evaluation of the difference)
const GAP_RANGES = [
    { min: 0, max: 20, label: 'Harmony', desc: '', tone: 'positive' },
    { min: 21, max: 45, label: 'Tension', desc: '', tone: 'challenging' },
    { min: 46, max: 70, label: 'Conflict', desc: '', tone: 'critical' },
    { min: 71, max: 100, label: 'Crisis', desc: '', tone: 'urgent' }
];

// Axis Intensity Ranges (Neurological State Definition)
const AXIS_RANGES = {
    S: [
        { max: 30, label: 'Low', status: '', desc: '' },
        { max: 65, label: 'Mid', status: '', desc: '' },
        { max: 100, label: 'High', status: '', desc: '' }
    ],
    E: [
        { max: 30, label: 'Low', status: '', desc: '' },
        { max: 65, label: 'Mid', status: '', desc: '' },
        { max: 100, label: 'High', status: '', desc: '' }
    ],
    B: [
        { max: 30, label: 'Low', status: '', desc: '' },
        { max: 65, label: 'Mid', status: '', desc: '' },
        { max: 100, label: 'High', status: '', desc: '' }
    ]
};

// --- 2. HELPER FUNCTIONS ---

const getTargetScore = (typeChar) => TARGET_POINTS[typeChar] || 50;

const getGapInfo = (gap) => {
    return GAP_RANGES.find(r => gap >= r.min && gap <= r.max) || GAP_RANGES[GAP_RANGES.length - 1];
};

const getAxisStatus = (axis, score) => {
    const config = AXIS_RANGES[axis] || AXIS_RANGES['B']; // Default to B structure if unknown
    return config.find(r => score <= r.max) || config[config.length - 1];
};

// --- 3. MAIN GENERATOR ---

/**
 * Generate Identity Catchphrase (Archetype x Beyond)
 */
const generateCatchphrase = (premiumType, beyondTypes) => {
    return "";
};

/**
 * Verbalize Self-Balance State (Replaces Gravity Field)
 */
const getSelfBalanceState = (megaScores, beyondScores, mode = 'general') => {
    return "";
};

/**
 * Generate Dynamic Analysis based on User defined Logic
 */
export const generateDynamicReport = (megaData, beyondData, beyondTitles, premiumType, mode = 'general') => {
    const isLoveMode = mode === 'love';
    const megaScores = megaData.score_seb || megaData.payload?.seb?.scores;
    const beyondTypes = beyondData || {}; // { S: 'A', E: 'B', B: 'C' }

    if (!megaScores || !beyondTypes.S) return null;

    // Mega: { S, E, F } -> Map F to B
    const reality = {
        S: Number(megaScores.S),
        E: Number(megaScores.E),
        B: Number(megaScores.F || megaScores.B) // Mapping Mega F to B, or use B if already B
    };

    const ideal = {
        S: getTargetScore(beyondTypes.S),
        E: getTargetScore(beyondTypes.E),
        B: getTargetScore(beyondTypes.B)
    };

    const axes = ['S', 'E', 'B'];
    const analysis = {};
    let maxGap = 0;

    axes.forEach(axis => {
        const rScore = reality[axis] || 0;
        const bType = beyondTypes[axis] || 'B';
        const iScore = getTargetScore(bType);

        const gap = Math.abs(rScore - iScore);
        if (gap > maxGap) maxGap = gap;

        const axisInfo = getAxisStatus(axis, rScore);
        const gapInfo = getGapInfo(gap);

        // --- Fetch Detailed Axis Data (New Integration) ---
        // Using Premium Type ID to find the corresponding Axis analysis
        const pId = premiumType?.id || 1;
        let axisDetail = null;
        let axisLoveInsight = "";

        // Select Data Source based on Axis
        const dataSource = axis === 'S' ? S_AXIS_DATA : (axis === 'E' ? E_AXIS_DATA : B_AXIS_DATA);
        // Correctly access localized data (assuming 'ko' for now or generic)
        const langData = dataSource?.ko || {};
        const typeData = langData[pId];

        if (typeData) {
            axisDetail = typeData.analysis; // Deep Analysis Text
            axisLoveInsight = typeData.loveInsight; // Love Insight Text
        }

        // --- NARRATIVE BRANCHING ---
        let axisSubtitle = "";
        let currentState = "";
        let targetState = "";
        let evolutionStrategy = "";
        let deepInsight = "";

        if (axis === 'S') {
            axisSubtitle = "";
            currentState = "";

            if (isLoveMode) {
                targetState = "";
                evolutionStrategy = "";
                deepInsight = "";
            } else {
                targetState = "";
                evolutionStrategy = "";
                deepInsight = "";
            }
        } else if (axis === 'E') {
            axisSubtitle = "";
            currentState = "";

            if (isLoveMode) {
                targetState = "";
                evolutionStrategy = "";
                deepInsight = "";
            } else {
                targetState = "";
                evolutionStrategy = "";
                deepInsight = "";
            }
        } else if (axis === 'B') {
            axisSubtitle = "";
            currentState = "";

            if (isLoveMode) {
                targetState = "";
                evolutionStrategy = "";
                deepInsight = "";
            } else {
                targetState = "";
                evolutionStrategy = "";
                deepInsight = "";
            }
        }

        // --- INTEGRATE DETAILED AXIS DATA ---
        let deepAnalysis = null;
        let loveAnalysis = null;

        if (axisDetail) {
            deepAnalysis = axisDetail;
        }
        if (axisLoveInsight && isLoveMode) {
            loveAnalysis = axisLoveInsight;
        }

        // Hormone Narrative
        let hormoneName = axis === 'S' ? "Serotonin" : (axis === 'E' ? "Oxytocin" : "Dopamine");
        let hormoneMission = "";
        if (isLoveMode) {
            hormoneMission = "";
        } else {
            hormoneMission = "";
        }

        let hormoneDesc = "";

        // Use Type-Specific Neuro Summary if available, else generic logic
        if (typeData && typeData.summary && typeData.title) {
            hormoneDesc = "";
        } else {
            hormoneDesc = "";
        }

        analysis[axis] = {
            score: rScore,
            idealScore: iScore,
            type: bType,
            gap,
            gapInfo,
            axisInfo,
            subtitle: axisSubtitle,
            expanded: {
                currentState,
                targetState,
                evolutionStrategy,
                deepInsight,
                deepAnalysis,
                loveAnalysis
            },
            hormone: { name: hormoneName, mission: hormoneMission, desc: hormoneDesc }
        };
    });

    // Causal Link Analysis (Interference v2) - Updated Terms
    let interference = null;

    // Modular Engine (v5.0 Tweaks)
    const twist = {
        rage: {
            title: isLoveMode ? "" : "",
            desc: ""
        },
        cognitive: {
            title: isLoveMode ? "" : "",
            desc: ""
        },
        empathy: {
            title: isLoveMode ? "" : "",
            desc: ""
        }
    };

    // Transformative Patches (v5.0)
    const patches = isLoveMode ? [
        { title: "", desc: "" },
        { title: "", desc: "" },
        { title: "", desc: "" }
    ] : [
        { title: "", desc: "" },
        { title: "", desc: "" },
        { title: "", desc: "" }
    ];

    // Final Expert Summary (Professional v5.4)
    let expertSummary = "";

    return {
        title: "",
        summary: getSelfBalanceState(reality, ideal, mode),
        catchphrase: generateCatchphrase(premiumType, beyondTypes),
        details: analysis,
        twist,
        interference,
        patches,
        expertSummary
    };
};
